import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { 
  ref, 
  uploadString, 
  getDownloadURL, 
  deleteObject, 
  uploadBytes 
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../firebase';
import { Photo } from '../types';
import { createThumbnail, createMediumImage } from '../utils/imageProcessing';

// Collection reference
const photosCollection = collection(db, 'photos');

// Get photos by project ID
export const getPhotosByProject = async (projectId: string): Promise<Photo[]> => {
  try {
    const q = query(
      photosCollection,
      where('projectId', '==', projectId),
      orderBy('takenAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const photos: Photo[] = [];
    
    querySnapshot.forEach((doc) => {
      photos.push({ id: doc.id, ...doc.data() } as Photo);
    });
    
    return photos;
  } catch (error) {
    console.error('Error getting photos by project:', error);
    throw error;
  }
};

// Get photos by room ID
export const getPhotosByRoom = async (projectId: string, roomId: string): Promise<Photo[]> => {
  try {
    const q = query(
      photosCollection,
      where('projectId', '==', projectId),
      where('roomId', '==', roomId),
      orderBy('takenAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const photos: Photo[] = [];
    
    querySnapshot.forEach((doc) => {
      photos.push({ id: doc.id, ...doc.data() } as Photo);
    });
    
    return photos;
  } catch (error) {
    console.error('Error getting photos by room:', error);
    throw error;
  }
};

// Get photos by item ID
export const getPhotosByItem = async (projectId: string, itemId: string): Promise<Photo[]> => {
  try {
    const q = query(
      photosCollection,
      where('projectId', '==', projectId),
      where('itemId', '==', itemId),
      orderBy('takenAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const photos: Photo[] = [];
    
    querySnapshot.forEach((doc) => {
      photos.push({ id: doc.id, ...doc.data() } as Photo);
    });
    
    return photos;
  } catch (error) {
    console.error('Error getting photos by item:', error);
    throw error;
  }
};

// Upload a photo. `source` is either a data URL (legacy callers, temporary
// photos) or a raw image Blob (the background upload queue).
export const uploadPhoto = async (
  projectId: string,
  source: string | Blob,
  options?: {
    roomId?: string;
    itemId?: string;
    notes?: string;
  }
): Promise<Photo> => {
  // Initialize variables for proper cleanup
  let blob: Blob | null = null;
  let mainBlob: Blob | null = null;
  let thumbnailBlob: Blob | null = null;
  let docRef: any = null;
  let mainUploaded = false;
  let thumbnailUploaded = false;
  let photoId: string | null = null;

  try {
    // Convert data URL to Blob when needed
    blob = typeof source === 'string' ? await (await fetch(source)).blob() : source;

    // Create optimized versions
    [mainBlob, thumbnailBlob] = await Promise.all([
      createMediumImage(blob),
      createThumbnail(blob)
    ]);

    // Filter out undefined values from options
    const filteredOptions: Record<string, any> = {};
    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined) {
          filteredOptions[key] = value;
        }
      });
    }

    // Create the photo document in Firestore first to get the document ID
    const photoData = {
      projectId,
      url: '', // Will be updated after upload
      thumbnail: '', // Will be updated after upload
      takenAt: Timestamp.now(),
      aiLens: { status: 'pending' as const }, // AI Lens will auto-analyze via Firestore trigger
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      ...filteredOptions
    };

    docRef = await addDoc(photosCollection, photoData);
    photoId = docRef.id;

    // Now use the Firestore document ID for storage paths
    const mainRef = ref(storage, `photos/${projectId}/${photoId}`);
    const thumbnailRef = ref(storage, `photos/${projectId}/${photoId}_thumbnail`);

    // Cache photos aggressively. Firebase Storage defaults to
    // `cache-control: private, max-age=0` when no metadata is set, so every
    // view re-downloaded the image from origin (the "slow even on fiber"
    // symptom). Each photo lives at an immutable path — edits create new docs,
    // and rotation is applied via CSS transform, never by overwriting the
    // blob — so it's safe to cache for a year as immutable.
    const photoUploadMetadata = { cacheControl: 'public, max-age=31536000, immutable' };

    // Upload the optimized main image
    await uploadBytes(mainRef, mainBlob, photoUploadMetadata);
    mainUploaded = true;
    const url = await getDownloadURL(mainRef);

    // Upload the thumbnail
    await uploadBytes(thumbnailRef, thumbnailBlob, photoUploadMetadata);
    thumbnailUploaded = true;
    const thumbnail = await getDownloadURL(thumbnailRef);

    // Update the document with the URLs
    await updateDoc(docRef, {
      url,
      thumbnail,
      updatedAt: serverTimestamp()
    });

    return {
      id: photoId!,
      ...photoData,
      url,
      thumbnail,
      takenAt: Timestamp.now() // Ensure takenAt is a Timestamp
    };
  } catch (error) {
    console.error('Error uploading photo:', error);

    // Clean up Storage blobs that were already uploaded before the failure.
    if (photoId && mainUploaded) {
      try {
        await deleteObject(ref(storage, `photos/${projectId}/${photoId}`));
      } catch (cleanupError) {
        console.error('Failed to cleanup main storage blob:', cleanupError);
      }
    }
    if (photoId && thumbnailUploaded) {
      try {
        await deleteObject(ref(storage, `photos/${projectId}/${photoId}_thumbnail`));
      } catch (cleanupError) {
        console.error('Failed to cleanup thumbnail storage blob:', cleanupError);
      }
    }

    // Clean up Firestore document if it was created but upload failed
    if (docRef) {
      try {
        await deleteDoc(docRef);
        console.log('Cleaned up Firestore document after upload failure');
      } catch (cleanupError) {
        console.error('Failed to cleanup Firestore document:', cleanupError);
      }
    }

    throw error;
  } finally {
    // 🧹 MEMORY LEAK FIX: Explicitly clean up all Blob objects
    if (blob) {
      blob = null;
    }
    if (mainBlob) {
      mainBlob = null;
    }
    if (thumbnailBlob) {
      thumbnailBlob = null;
    }
    if (docRef) {
      docRef = null;
    }
  }
};

// Helper function to extract storage path from URL
const getStoragePathFromUrl = (url: string): string | null => {
  try {
    // Firebase Storage URLs have the format:
    // https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?{params}
    const urlObj = new URL(url);
    const pathParam = urlObj.pathname.split('/o/')[1];
    
    if (pathParam) {
      // Decode the path and remove query parameters
      return decodeURIComponent(pathParam.split('?')[0]);
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting storage path from URL:', error);
    return null;
  }
};

// Delete all photos for a project
export const deleteAllPhotosForProject = async (projectId: string): Promise<{
  deletedCount: number;
  errors: string[];
}> => {
  try {
    console.log(`🗑️ Starting batch deletion for project ${projectId}`);
    
    // Get all photos for the project
    const photos = await getPhotosByProject(projectId);
    console.log(`📸 Found ${photos.length} photos to delete`);
    
    let deletedCount = 0;
    const errors: string[] = [];
    
    // Delete photos in batches to avoid overwhelming the system
    const batchSize = 5;
    for (let i = 0; i < photos.length; i += batchSize) {
      const batch = photos.slice(i, i + batchSize);
      
      // Delete photos in parallel within each batch
      const batchPromises = batch.map(async (photo) => {
        try {
          await deletePhoto(photo.id, projectId);
          console.log(`✅ Deleted photo ${photo.id}`);
          return { success: true, photoId: photo.id };
        } catch (error) {
          const errorMsg = `Failed to delete photo ${photo.id}: ${error}`;
          console.error(errorMsg);
          return { success: false, photoId: photo.id, error: errorMsg };
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      
      // Count successes and collect errors
      batchResults.forEach(result => {
        if (result.success) {
          deletedCount++;
        } else {
          errors.push(result.error || 'Unknown error');
        }
      });
      
      // Small delay between batches to be gentle on the system
      if (i + batchSize < photos.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    console.log(`🎉 Batch deletion completed: ${deletedCount} deleted, ${errors.length} errors`);
    
    return { deletedCount, errors };
  } catch (error) {
    console.error('Error in batch photo deletion:', error);
    throw error;
  }
};

// Enhanced delete photo function with better error handling and cache busting
export const deletePhoto = async (photoId: string, projectId: string): Promise<void> => {
  try {
    console.log(`🗑️ Deleting photo ${photoId} from project ${projectId}`);
    
    // Get the photo document
    const photoDoc = await getDoc(doc(photosCollection, photoId));
    
    if (!photoDoc.exists()) {
      console.warn(`Photo with ID ${photoId} not found in database`);
      return; // Photo already deleted or doesn't exist
    }
    
    const photoData = photoDoc.data() as Photo;
    console.log(`📸 Photo found: ${photoData.url}`);
    
    // Extract storage paths from the URLs instead of guessing
    const mainStoragePath = getStoragePathFromUrl(photoData.url);
    const thumbnailStoragePath = photoData.thumbnail ? getStoragePathFromUrl(photoData.thumbnail) : null;
    
    console.log(`🗂️ Storage paths - Main: ${mainStoragePath}, Thumbnail: ${thumbnailStoragePath}`);

    // Delete the Firestore document FIRST. If this fails we abort before touching
    // Storage, so a failed delete can't leave Storage blobs orphaned (no doc left
    // pointing at them). A small Storage blob outliving its doc is far worse than
    // a doc whose blob delete is retried, since orphaned blobs are unreachable.
    await deleteDoc(doc(photosCollection, photoId));
    console.log(`✅ Photo document deleted from Firestore`);

    // Now best-effort delete the Storage blobs. Failures here are non-fatal:
    // the doc is already gone, so at worst a blob is briefly orphaned and can be
    // swept later — we don't rethrow and resurrect the inconsistency.
    if (mainStoragePath) {
      try {
        await deleteObject(ref(storage, mainStoragePath));
        console.log(`✅ Main photo deleted from storage`);
      } catch (storageError: any) {
        if (storageError.code !== 'storage/object-not-found') {
          console.error('Error deleting main photo from storage (doc already removed):', storageError);
        } else {
          console.log(`ℹ️ Main photo already deleted from storage`);
        }
      }
    }

    if (thumbnailStoragePath) {
      try {
        await deleteObject(ref(storage, thumbnailStoragePath));
        console.log(`✅ Thumbnail deleted from storage`);
      } catch (storageError: any) {
        if (storageError.code !== 'storage/object-not-found') {
          console.error('Error deleting thumbnail from storage (doc already removed):', storageError);
        } else {
          console.log(`ℹ️ Thumbnail already deleted from storage`);
        }
      }
    }

  } catch (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
};

/**
 * Error thrown when the offline photo localStorage queue is full.
 * iOS Safari caps localStorage at ~5MB; a single high-res HEIC base64 can
 * exceed that on its own. Callers should catch this and surface a clear
 * "offline queue full" message to the user so photos aren't silently lost.
 */
export class OfflineQuotaExceededError extends Error {
  code = 'offline_quota_exceeded';
  constructor(message = 'Offline photo queue is full — connect to Wi-Fi to upload pending photos before taking more.') {
    super(message);
    this.name = 'OfflineQuotaExceededError';
  }
}

const isQuotaExceededError = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false;
  // Chrome: QuotaExceededError; Safari: QUOTA_EXCEEDED_ERR; Firefox: NS_ERROR_DOM_QUOTA_REACHED
  return (
    err.name === 'QuotaExceededError' ||
    err.name === 'QUOTA_EXCEEDED_ERR' ||
    err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    /quota/i.test(err.message)
  );
};

// Store photo locally when offline
export const storeOfflinePhoto = (
  projectId: string,
  dataUrl: string,
  options?: {
    roomId?: string;
    itemId?: string;
    notes?: string;
  }
): string => {
  // Generate a unique ID for the photo
  const photoId = uuidv4();

  // Filter out undefined values from options
  const filteredOptions: Record<string, any> = {};
  if (options) {
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined) {
        filteredOptions[key] = value;
      }
    });
  }

  // Create an offline photo object
  const offlinePhoto = {
    id: photoId,
    projectId,
    dataUrl,
    takenAt: new Date().getTime(),
    offline: true,
    ...filteredOptions
  };

  // Get existing offline photos
  const offlinePhotos = JSON.parse(localStorage.getItem('offlinePhotos') || '[]');
  offlinePhotos.push(offlinePhoto);

  try {
    localStorage.setItem('offlinePhotos', JSON.stringify(offlinePhotos));
  } catch (error) {
    if (isQuotaExceededError(error)) {
      console.error('Offline photo queue full — localStorage quota exceeded');
      throw new OfflineQuotaExceededError();
    }
    console.error('Error storing offline photo:', error);
    throw error;
  }

  return photoId;
};

// Track deleted photos to prevent sync conflicts
export const markOfflinePhotoAsDeleted = (photoId: string): void => {
  try {
    // Get existing deleted photos tracking
    const deletedPhotos = JSON.parse(localStorage.getItem('deletedOfflinePhotos') || '[]');
    
    // Add the photo ID to deleted list
    if (!deletedPhotos.includes(photoId)) {
      deletedPhotos.push(photoId);
      localStorage.setItem('deletedOfflinePhotos', JSON.stringify(deletedPhotos));
    }
  } catch (error) {
    console.error('Error marking offline photo as deleted:', error);
  }
};

// Sync offline photos when back online
export const syncOfflinePhotos = async (): Promise<void> => {
  try {
    // Get offline photos
    const offlinePhotos = JSON.parse(localStorage.getItem('offlinePhotos') || '[]');
    
    if (offlinePhotos.length === 0) return;
    
    // Get deleted photos tracking
    const deletedPhotos = JSON.parse(localStorage.getItem('deletedOfflinePhotos') || '[]');
    
    console.log(`🔄 Syncing ${offlinePhotos.length} offline photos (${deletedPhotos.length} marked as deleted)`);

    // Cache project existence checks to avoid repeat fetches for the same project
    const projectExistsCache = new Map<string, boolean>();
    const projectExists = async (pid: string): Promise<boolean> => {
      const cached = projectExistsCache.get(pid);
      if (cached !== undefined) return cached;
      try {
        const snap = await getDoc(doc(db, 'projects', pid));
        const exists = snap.exists();
        projectExistsCache.set(pid, exists);
        return exists;
      } catch (err) {
        console.error(`Project existence check failed for ${pid}:`, err);
        // If the check itself failed, assume it exists so we don't discard data.
        return true;
      }
    };

    // Upload each photo (excluding deleted ones).
    // A photo is removed from the queue only if it was successfully uploaded,
    // intentionally skipped (deleted), or discarded (project gone). Photos whose
    // upload FAILS are retained so the next sync can retry them — they must not be lost.
    const resolvedIds = new Set<string>();
    let syncedCount = 0;
    let discardedCount = 0;
    let failedCount = 0;
    for (const photo of offlinePhotos) {
      // Skip photos that were marked as deleted (resolve: drop from queue)
      if (deletedPhotos.includes(photo.id)) {
        console.log(`⏭️ Skipping deleted offline photo ${photo.id}`);
        resolvedIds.add(photo.id);
        continue;
      }

      // Discard photos whose project no longer exists (resolve: drop from queue)
      if (!(await projectExists(photo.projectId))) {
        console.warn(`⚠️ Discarding offline photo ${photo.id}: project ${photo.projectId} no longer exists`);
        resolvedIds.add(photo.id);
        discardedCount++;
        continue;
      }

      try {
        // Filter out undefined values from options
        const options: Record<string, any> = {};

        if (photo.roomId !== undefined) options.roomId = photo.roomId;
        if (photo.itemId !== undefined) options.itemId = photo.itemId;
        if (photo.notes !== undefined) options.notes = photo.notes;

        console.log(`📤 Uploading offline photo ${photo.id}`);
        await uploadPhoto(
          photo.projectId,
          photo.dataUrl,
          options
        );
        console.log(`✅ Successfully synced offline photo ${photo.id}`);
        resolvedIds.add(photo.id);
        syncedCount++;
      } catch (error) {
        // Do NOT resolve — leave this photo in the queue for the next retry.
        console.error(`❌ Error syncing offline photo ${photo.id} (will retry on next sync):`, error);
        failedCount++;
      }
    }

    // Keep only photos that were neither uploaded, skipped, nor discarded.
    const remainingPhotos = offlinePhotos.filter((p: { id: string }) => !resolvedIds.has(p.id));
    localStorage.setItem('offlinePhotos', JSON.stringify(remainingPhotos));
    // Prune deleted-tracking entries that have now been resolved out of the queue.
    const remainingDeleted = deletedPhotos.filter(
      (id: string) => remainingPhotos.some((p: { id: string }) => p.id === id)
    );
    localStorage.setItem('deletedOfflinePhotos', JSON.stringify(remainingDeleted));

    console.log(
      `🎉 Offline photo sync completed — ${syncedCount} synced, ${discardedCount} discarded, ${failedCount} failed (retained for retry)`
    );
  } catch (error) {
    console.error('Error syncing offline photos:', error);
    throw error;
  }
};

// Get a single photo by ID
export const getPhoto = async (photoId: string): Promise<Photo> => {
  try {
    const photoDoc = await getDoc(doc(photosCollection, photoId));
    
    if (!photoDoc.exists()) {
      throw new Error(`Photo with ID ${photoId} not found`);
    }
    
    return { id: photoDoc.id, ...photoDoc.data() } as Photo;
  } catch (error) {
    console.error('Error getting photo:', error);
    throw error;
  }
};

// Store a temporary photo (before project creation)
export const storeTemporaryPhoto = (
  dataUrl: string,
  notes?: string
): string => {
  try {
    // Generate a unique ID for the photo
    const photoId = uuidv4();
    
    // Create a temporary photo object
    const tempPhoto = {
      id: photoId,
      dataUrl,
      notes: notes || '',
      takenAt: new Date().getTime()
    };
    
    // Get existing temporary photos
    const tempPhotos = JSON.parse(localStorage.getItem('temporaryPhotos') || '[]');
    
    // Add the new photo
    tempPhotos.push(tempPhoto);
    
    // Save back to localStorage
    localStorage.setItem('temporaryPhotos', JSON.stringify(tempPhotos));
    
    return photoId;
  } catch (error) {
    console.error('Error storing temporary photo:', error);
    throw error;
  }
};

// Get all temporary photos
export const getTemporaryPhotos = (): Array<{
  id: string;
  dataUrl: string;
  notes: string;
  takenAt: number;
}> => {
  try {
    return JSON.parse(localStorage.getItem('temporaryPhotos') || '[]');
  } catch (error) {
    console.error('Error getting temporary photos:', error);
    return [];
  }
};

// Clear all temporary photos
export const clearTemporaryPhotos = (): void => {
  localStorage.removeItem('temporaryPhotos');
};

// Associate temporary photos with a project
export const associateTemporaryPhotos = async (projectId: string, itemId?: string): Promise<string[]> => {
  try {
    const tempPhotos = getTemporaryPhotos();
    const uploadedPhotoIds: string[] = [];
    
    // Upload each temporary photo
    for (const tempPhoto of tempPhotos) {
      try {
        const photo = await uploadPhoto(projectId, tempPhoto.dataUrl, {
          notes: tempPhoto.notes || undefined,
          itemId: itemId // Pass the itemId if provided
        });
        uploadedPhotoIds.push(photo.id);
      } catch (err) {
        console.error('Error uploading temporary photo:', err);
      }
    }
    
    // Clear temporary photos after uploading
    clearTemporaryPhotos();
    
    return uploadedPhotoIds;
  } catch (error) {
    console.error('Error associating temporary photos:', error);
    throw error;
  }
};

// Trigger AI Lens analysis on an existing photo
export const triggerLensAnalysis = async (
  photoId: string,
  photoUrl: string,
  projectId: string,
  roomId?: string
): Promise<void> => {
  try {
    const analyzeLensPhoto = httpsCallable(functions, 'analyzeLensPhoto');
    await analyzeLensPhoto({ photoId, photoUrl, projectId, roomId });
  } catch (error) {
    console.error('Error triggering AI Lens analysis:', error);
    throw error;
  }
}; 